import { captureException } from '@sentry/react'
import {
  generateAIVideo_RecommendationApi,
  generateConsistentAIVideo_API,
  generateSmartEditedAIVideo_API
} from 'api'
import { PROJECT_MEDIA_TYPES, SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { SUB_SIDEBAR_KEYS } from 'consts/sidebar'
import { ErrorKeys } from 'consts/errorKeys'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import {
  buildAIVideoMediaItem,
  getFirstFrameOrRefImageUrlFromApi,
  updateAiImageMediaItemToVideoGeneration
} from 'store/features/aiVideo/utils'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { isSmartEditModeSelector } from 'store/selectors'
import { withAppThunks } from 'store/with-app-thunks'
import { TAppDispatch, TGetState } from 'store/types'
import { handleApiError } from 'store/utils'
import { IAIVideoGenerationRequestBody, IAIVideoProject } from 'types/aiVideo'
import { IMediaItem, MediaStatus } from 'types/media'
import { getDeviceVersion, scrollToElementById } from 'utils'
import { ApiError, ReduxThunkError } from 'utils/errors'
import { getLastMediaItemEndTime } from 'utils/media'
import { generationProgressService } from 'services/generationProgress'
import { aiLibThunks } from 'store/features/aiLibVideo/thunks'

const _handleApiError = (error: unknown) => (dispatch: TAppDispatch) => {
  if (!(error instanceof ApiError)) {
    captureException(new ReduxThunkError(error))
  }

  if (
    (error as Error & { cause?: { code?: string } })?.cause?.code ===
    'ModerationError'
  ) {
    return dispatch(handleApiError(ErrorKeys.AI_MODERATION_FAILED))
  }

  dispatch(handleApiError(ErrorKeys.AI_VIDEO_GENERATION_FAILED))
}

const buildCacheEntry = (
  genAiVideoHash: string,
  request: IAIVideoGenerationRequestBody
): IAIVideoProject => ({
  id: genAiVideoHash,
  referenceMedias: request.referenceMedias || { images: [] },
  userId: 0,
  prompt: request.prompt,
  status: null,
  aspectRatio: request.aspectRatio ?? null,
  quality: request.quality,
  videoDurationSeconds: request.videoDurationSeconds,
  videoSource: '',
  videoModel: request.videoModel,
  videoUrl: '',
  generatedBy: '',
  width: null,
  height: null,
  meta: request.meta
})

interface IAIVideoGenerationPayload {
  genAiVideoHash: string
  generationRequest: IAIVideoGenerationRequestBody
  insertionStartTime?: number
}

const _handleGenerateAiVideo = ({
  genAiVideoHash,
  generationRequest,
  insertionStartTime
}: IAIVideoGenerationPayload) =>
  withAppThunks(
    appThunks => async (dispatch: TAppDispatch, getState: TGetState) => {
      const state = getState()
      const defaultDimensions = projectDataSelectors.defaultDimensions(state)
      const mediaItems = projectDataSelectors.mediaItems(state)
      const expectedGenerationDuration =
        aiVideoSelectors.expectedGenerationDuration(state)
      const lastEndTime = getLastMediaItemEndTime(mediaItems)

      dispatch(
        appThunks.cacheThunks.addToAiVideoCache([
          buildCacheEntry(genAiVideoHash, generationRequest)
        ])
      )

      const mediaItem = await buildAIVideoMediaItem({
        referenceImageUrl: getFirstFrameOrRefImageUrlFromApi(
          generationRequest.referenceMedias
        ),
        videoDurationSeconds: generationRequest.videoDurationSeconds,
        insertionStartTime: insertionStartTime ?? lastEndTime,
        id: genAiVideoHash,
        assetId: genAiVideoHash,
        width: defaultDimensions.width,
        height: defaultDimensions.height,
        defaultDimensions
      })
      generationProgressService.start(
        mediaItem.id,
        mediaItem.assetId,
        expectedGenerationDuration,
        true,
        0
      )

      dispatch(appThunks.aiVideoThunks.setTargetMediaItemId(genAiVideoHash))
      dispatch(
        appThunks.projectDataThunks.addMediaItems({ value: [mediaItem] })
      )
      dispatch(
        appThunks.editorThunks.setActiveSidebarTab(SUB_SIDEBAR_KEYS.AI_VIDEO)
      )
      dispatch(
        appThunks.editorThunks.setActiveTimelineItems([
          {
            id: genAiVideoHash,
            type: PROJECT_MEDIA_TYPES.aiVideo
          }
        ])
      )

      scrollToElementById(genAiVideoHash)
      dispatch(aiLibThunks.setIsLoaded(false))
    }
  )

interface IVideoRegenerationParams {
  timelineItemId: string
  genAiVideoHash: string
  generationRequestPayload: IAIVideoGenerationRequestBody
}
const _handleRegenerateAIVideo = ({
  timelineItemId,
  genAiVideoHash,
  generationRequestPayload
}: IVideoRegenerationParams) =>
  withAppThunks(appThunks => (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const mediaItems = projectDataSelectors.mediaItems(state)
    const expectedGenerationDuration =
      aiVideoSelectors.expectedGenerationDuration(state)
    const processingItem = mediaItems.find(item => item.id === timelineItemId)
    const { referenceMedias, videoDurationSeconds } = generationRequestPayload
    const imageUrl = getFirstFrameOrRefImageUrlFromApi(referenceMedias)
    dispatch(
      appThunks.editorThunks.setActiveSidebarTab(SUB_SIDEBAR_KEYS.AI_VIDEO)
    )
    dispatch(
      appThunks.cacheThunks.addToAiVideoCache([
        buildCacheEntry(genAiVideoHash, generationRequestPayload)
      ])
    )

    if (processingItem) {
      dispatch(
        appThunks.projectDataThunks.saveProcessingItemData(processingItem)
      )
      generationProgressService.start(
        processingItem.id,
        String(genAiVideoHash),
        expectedGenerationDuration,
        true,
        0
      )
      dispatch(
        appThunks.projectDataThunks.updateMediaItem({
          id: processingItem.id,
          updates: {
            ...processingItem,
            assetId: genAiVideoHash,
            type: PROJECT_MEDIA_TYPES.aiVideo,
            meta: {
              ...processingItem.meta,
              path: imageUrl || processingItem?.meta?.path || null,
              originalDuration: videoDurationSeconds,
              status: MediaStatus.PENDING,
              trimStart: 0
            }
          }
        })
      )
    }
    dispatch(aiLibThunks.setIsLoaded(false))
  })

const generateConsistentAIVideo = (insertionStartTime: number = 0) =>
  withAppThunks(
    appThunks => async (dispatch: TAppDispatch, getState: TGetState) => {
      const state = getState()
      const isConsistencyEnabled = aiVideoSelectors.isConsistencyEnabled(state)
      if (!isConsistencyEnabled) return

      const isMobile = getDeviceVersion(SMALL_DESKTOP_SIZE_BREAKPOINT)
      const generationRequestPayload = dispatch(
        appThunks.aiVideoThunks.getPreparedRequestPayload()
      )

      dispatch(appThunks.aiVideoThunks.setIsRequestingGeneration(true))
      if (isMobile) {
        dispatch(appThunks.editorThunks.setIsMobileSubSidebarOpen(false))
      }

      try {
        const response = await generateConsistentAIVideo_API(
          generationRequestPayload
        )
        const data = response.data
        if (data?.genAiVideoHash) {
          dispatch(
            _handleGenerateAiVideo({
              genAiVideoHash: data.genAiVideoHash,
              generationRequest: generationRequestPayload,
              insertionStartTime
            })
          )
        }
      } catch (error) {
        dispatch(_handleApiError(error))
      } finally {
        dispatch(appThunks.aiVideoThunks.setIsRequestingGeneration(false))
      }
    }
  )

const generateSmartEditedAIVideo = () =>
  withAppThunks(
    appThunks => async (dispatch: TAppDispatch, getState: TGetState) => {
      const state = getState()
      const isMobile = getDeviceVersion(SMALL_DESKTOP_SIZE_BREAKPOINT)
      const isSmartEditMode = isSmartEditModeSelector(state)
      const hasSmartPrompt = aiVideoSelectors.smartPrompt(state)
      const timelineItemId = aiVideoSelectors.targetMediaItemId(state)

      if (!isSmartEditMode || !hasSmartPrompt || !timelineItemId) return

      dispatch(appThunks.aiVideoThunks.setIsRequestingGeneration(true))
      if (isMobile) {
        dispatch(appThunks.editorThunks.setIsMobileSubSidebarOpen(false))
      }

      const generationRequestPayload = dispatch(
        appThunks.aiVideoThunks.getPreparedRequestPayload()
      )

      try {
        const response = await generateSmartEditedAIVideo_API(
          generationRequestPayload
        )
        const genAiVideoHash = response?.data?.genAiVideoHash
        if (genAiVideoHash) {
          dispatch(
            _handleRegenerateAIVideo({
              genAiVideoHash,
              generationRequestPayload,
              timelineItemId
            })
          )
          dispatch(appThunks.aiVideoThunks.setSmartPrompt(''))
          dispatch(appThunks.aiVideoThunks.clearGenerationParams())
          dispatch(appThunks.editorThunks.setAsideOverlayContent(null))
          dispatch(
            appThunks.editorThunks.setActiveSidebarTab(
              SUB_SIDEBAR_KEYS.AI_VIDEO
            )
          )
        }
      } catch (error) {
        dispatch(_handleApiError(error))
      } finally {
        dispatch(appThunks.aiVideoThunks.setIsRequestingGeneration(false))
      }
    }
  )

interface IInitGeneration {
  genAiVideoHash: string
  generationRequestPayload: IAIVideoGenerationRequestBody
  targetMediaItem: IMediaItem | undefined
}

const _initGeneration = () => {
  return withAppThunks(
    appThunks =>
      async (
        dispatch: TAppDispatch,
        getState: TGetState
      ): Promise<IInitGeneration> => {
        const state = getState()
        const isMobile = getDeviceVersion(SMALL_DESKTOP_SIZE_BREAKPOINT)
        const targetMediaItemId = aiVideoSelectors.targetMediaItemId(state)
        const mediaItems = projectDataSelectors.mediaItems(state)
        const targetMediaItem = mediaItems.find(
          item => item.id === targetMediaItemId
        )
        dispatch(appThunks.aiVideoThunks.setIsRequestingGeneration(true))
        if (targetMediaItem) {
          dispatch(
            appThunks.projectDataThunks.saveProcessingItemData(targetMediaItem)
          )
        }
        if (isMobile) {
          dispatch(appThunks.editorThunks.setIsMobileSubSidebarOpen(false))
        }

        try {
          const projectId = await dispatch(
            appThunks.projectThunks.getOrCreateProjectId()
          )
          const generationRequestPayload = dispatch(
            appThunks.aiVideoThunks.getPreparedRequestPayload(projectId)
          )
          const response = await generateAIVideo_RecommendationApi(
            generationRequestPayload
          )
          const genAiVideoHash = response?.data?.genAiVideoHash
          return { genAiVideoHash, generationRequestPayload, targetMediaItem }
        } catch (error) {
          // for image instant animation, we fake the initial generation data to get the loading state before getting the actual generation response,
          // so need to handle the failed generation for the existing item outside of the socket
          if (targetMediaItem) {
            dispatch(
              appThunks.projectDataThunks.handleFailedGenerationForMediaItem(
                targetMediaItem.id
              )
            )
          }
          // to catch by gen/regen thunks
          throw error
        }
      }
  )
}

const _animateImageToVideo = (
  targetMediaItem: IMediaItem,
  genAiVideoHash: string,
  generationRequestPayload: IAIVideoGenerationRequestBody
) =>
  withAppThunks(
    appThunks => async (dispatch: TAppDispatch, getState: TGetState) => {
      const state = getState()
      const defaultDimensions = projectDataSelectors.defaultDimensions(state)
      const videoModels = aiVideoSelectors.aiVideoModels(state)

      const { videoDurationSeconds, videoModel } = generationRequestPayload
      const isAudioSupported =
        videoModels.find(model => model.model === videoModel)?.capabilities
          .isAudioGenerationSupported || false
      dispatch(
        appThunks.editorThunks.setActiveSidebarTab(SUB_SIDEBAR_KEYS.AI_VIDEO)
      )
      dispatch(
        appThunks.projectDataThunks.updateMediaItem({
          id: targetMediaItem.id,
          updates: updateAiImageMediaItemToVideoGeneration(
            targetMediaItem,
            genAiVideoHash,
            defaultDimensions,
            videoDurationSeconds,
            isAudioSupported
          )
        })
      )
      generationProgressService.start(
        targetMediaItem.id,
        genAiVideoHash,
        aiVideoSelectors.expectedGenerationDuration(state),
        true,
        0
      )
      dispatch(aiLibThunks.setIsLoaded(false))
    }
  )

const generateAiVideo = (insertionStartTime: number = 0) =>
  withAppThunks(appThunks => async (dispatch: TAppDispatch) => {
    try {
      const { genAiVideoHash, generationRequestPayload, targetMediaItem } =
        (await dispatch(_initGeneration())) as IInitGeneration

      const isImageAnimationTarget =
        targetMediaItem && targetMediaItem.type !== PROJECT_MEDIA_TYPES.aiVideo
      if (isImageAnimationTarget) {
        dispatch(
          _animateImageToVideo(
            targetMediaItem,
            genAiVideoHash,
            generationRequestPayload
          )
        )
        return
      }
      if (genAiVideoHash) {
        dispatch(
          _handleGenerateAiVideo({
            genAiVideoHash,
            generationRequest: generationRequestPayload,
            insertionStartTime
          })
        )
        return
      }
    } catch (error) {
      dispatch(_handleApiError(error))
    } finally {
      dispatch(appThunks.aiVideoThunks.setIsRequestingGeneration(false))
    }
  })
const regenerateAiVideo = () =>
  withAppThunks(appThunks => async (dispatch: TAppDispatch) => {
    try {
      const { genAiVideoHash, generationRequestPayload, targetMediaItem } =
        (await dispatch(_initGeneration())) as IInitGeneration

      if (
        !targetMediaItem ||
        targetMediaItem.type !== PROJECT_MEDIA_TYPES.aiVideo
      ) {
        throw new Error('No AI video item selected for regeneration')
      }

      if (genAiVideoHash) {
        dispatch(
          _handleRegenerateAIVideo({
            genAiVideoHash,
            generationRequestPayload,
            timelineItemId: targetMediaItem.id
          })
        )
      }
    } catch (error) {
      dispatch(_handleApiError(error))
    } finally {
      dispatch(appThunks.aiVideoThunks.setIsRequestingGeneration(false))
    }
  })

export const smartGenerationThunks = {
  generateConsistentAIVideo,
  generateSmartEditedAIVideo,
  generateAiVideo,
  regenerateAiVideo
}
